import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {INFLUENCER_KEYS} from "@/api/influencerApi";
import {MEDIA_KEYS} from "@/api/mediaApi";
import {PUNISHMENT_KEYS} from "@/api/punishmentApi";
import type {CampaignManagementInfluencerSerializer} from "@/generated/Influencer";
import type {PunishmentSerializer} from "@/generated/Punishment";
import {useGenericInfiniteQueryWithCount} from "@/hooks/useQueryHooks";
import {useSelectedCompanyId} from "@/hooks/useUserHooks";
import useCampaignManagementStore, {useManagedCampaignType} from "@/store/campaignManagement";
import useInfluencerNetworkStore from "@/store/influencerNetwork";
import {disciplinaryModalAtom, disciplineTabAtom, isAdminFeedbackModalOpenAtom} from "@/store/jotaiAtoms";
import type {
  TDashboardCampaign,
  TDisciplineCampaign,
  TDisciplineTab,
  TInfluencerProfileDetail,
  TPunishmentType,
} from "@/store/types";
import {useQueryClient} from "@tanstack/react-query";
import {useAtom, useAtomValue} from "jotai";
import {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router";

const DISCIPLINE_SEVERITY: Record<TPunishmentType, number> = {
  UN_VERIFIED_INSTAGRAM: 0,
  UN_VERIFIED_TIKTOK: 0,
  UN_VERIFIED_UGC: 0,
  PROBATION: 1,
  TIMED_BAN: 2,
  PERMANENT_BAN: 3,
};

const useDiscipline = () => {
  const [disciplinaryData, setDisciplineModalData] = useAtom(disciplinaryModalAtom);
  const disciplineTab = useAtomValue(disciplineTabAtom);
  const {t} = useTranslation("influencer_discipline");
  const campaignType = useManagedCampaignType();
  const isFeedbackModalOpen = useAtomValue(isAdminFeedbackModalOpenAtom);
  const location = useLocation();
  const tabState = useCampaignManagementStore(state => state.tabState);
  const queryClient = useQueryClient();
  const companyId = useSelectedCompanyId();
  const campaign = useCampaignManagementStore(state => state.campaign);
  const influencerNetworkSearchParams = useInfluencerNetworkStore(state => state.searchParams);

  const {data: activeCampaigns} = useGenericInfiniteQueryWithCount<{
    campaign: TDashboardCampaign;
  }>({
    queryKey: INFLUENCER_KEYS.qInfluencerActiveCampaigns(disciplinaryData?.influencer.id!),
    enabled: !!disciplinaryData?.influencer.id,
  });

  const disciplineTabInPunishmentType: TPunishmentType = useMemo(() => {
    if (disciplineTab === "UNVERIFY") {
      return disciplinaryData?.selectedUnverificationType === "instagram"
        ? "UN_VERIFIED_INSTAGRAM"
        : disciplinaryData?.selectedUnverificationType === "tiktok"
          ? "UN_VERIFIED_TIKTOK"
          : "UN_VERIFIED_UGC";
    }

    return disciplineTab;
  }, [disciplineTab, disciplinaryData?.selectedUnverificationType]);

  const getUnverificationPlatformLabel = useCallback(
    (type?: TPunishmentType) => {
      switch (type) {
        case "UN_VERIFIED_INSTAGRAM":
          return t("common:instagram");
        case "UN_VERIFIED_TIKTOK":
          return t("common:tiktok");
        case "UN_VERIFIED_UGC":
          return t("common:ugc");
        default:
          return "";
      }
    },
    [t],
  );

  const getDisciplinaryModalPreviousLocation = useCallback(() => {
    if (isFeedbackModalOpen) {
      return t("feedback");
    } else if (tabState === "active") {
      return t("active");
    } else if (tabState === "reporting") {
      return t("reporting");
    } else if (location.pathname.includes("/influencers-list")) {
      return t("influencers");
    } else if (location.pathname.includes("/influencer/")) {
      return t("influencer");
    } else if (location.pathname.includes("/removed/")) {
      return t("removed");
    } else if (location.pathname.includes("/preparation/")) {
      return t("preparation");
    } else {
      return t("back");
    }
  }, [isFeedbackModalOpen, location.pathname, tabState]);

  const getLabelByDisciplineType = useCallback((disciplineType: TDisciplineTab | TPunishmentType) => {
    if (disciplineType === "PROBATION") {
      return t("probation");
    } else if (disciplineType === "TIMED_BAN") {
      return t("timed_ban");
    } else if (disciplineType === "PERMANENT_BAN") {
      return t("ban");
    }

    return t("unverify_influencer");
  }, []);

  const isStrongerDisciplineActive = useMemo(() => {
    if (disciplineTab === "UNVERIFY" || !disciplinaryData.lastDiscipline?.punishment_type) {
      return false;
    }

    return (
      DISCIPLINE_SEVERITY[disciplinaryData.lastDiscipline?.punishment_type] >
      DISCIPLINE_SEVERITY[disciplineTabInPunishmentType]
    );
  }, [disciplineTab, disciplinaryData.lastDiscipline?.punishment_type]);

  const initializeDisciplineModal = useCallback(
    ({
      campaignApplication,
      influencer,
      selectedPunishment,
    }: {
      campaignApplication?: TDisciplineCampaign;
      influencer: CampaignManagementInfluencerSerializer | TInfluencerProfileDetail;
      selectedPunishment?: PunishmentSerializer | null;
    }) => {
      const {user} = influencer;

      setDisciplineModalData({
        campaignApplication,
        influencer: {
          firstName: user.first_name,
          id: influencer.uuid!,
          image: influencer.picture320,
          isInstagramVerified: user.is_verified,
          isTiktokVerified: user.tiktok_verified,
          isUgcVerified: user.ugc_verified,
          username: campaignType === "tiktok" ? user.tiktok_username! : user.username!,
        },
        instagramDiscipline: user.instagram_unverified,
        lastDiscipline: user.last_punishment,
        selectedDiscipline: selectedPunishment,
        tiktokDiscipline: user.tiktok_unverified,
        ugcDiscipline: user.ugc_unverified,
        userId: user.id,
      });
    },
    [campaignType, setDisciplineModalData],
  );

  const invalidateQueries = useCallback(() => {
    const influencerParams = location.search.slice(1);
    [
      CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id!),
      CAMPAIGN_KEYS.qCampaignCancelledApplications(campaign.id!),
      CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id!),
      CAMPAIGN_KEYS.qCampaignPreparationApplications(campaign.id!, true),
      CAMPAIGN_KEYS.qCampaignReportingApplications({campaignId: campaign.id!}).slice(0, 2),
      INFLUENCER_KEYS.qInfluencer(
        disciplinaryData.influencer.id!,
        `${influencerParams ?? ""}${!!influencerParams?.length ? "&" : ""}${!!companyId ? "company_id=" + companyId : ""}`,
      ),
      INFLUENCER_KEYS.qInfluencerList(influencerNetworkSearchParams),
      MEDIA_KEYS.qContentFeedback({campaignId: campaign.id!}),
    ].map(queryKey => queryClient.invalidateQueries({queryKey}));
    queryClient.refetchQueries({queryKey: PUNISHMENT_KEYS.qPunishments(disciplinaryData?.userId!)});
  }, [
    campaign.id,
    companyId,
    disciplinaryData.influencer.id,
    disciplinaryData.userId,
    influencerNetworkSearchParams,
    location.search,
    queryClient,
  ]);

  const setDisciplineByTab = useCallback(
    (tab: TDisciplineTab) => {
      setDisciplineModalData(draft => {
        draft.selectedDiscipline =
          tab === "UNVERIFY"
            ? undefined
            : disciplinaryData.lastDiscipline?.punishment_type === tab
              ? disciplinaryData.lastDiscipline
              : undefined;
      });
    },
    [disciplinaryData.lastDiscipline, setDisciplineModalData],
  );

  return {
    activeCampaignsCount: activeCampaigns?.count,
    disciplineTabInPunishmentType,
    getDisciplinaryModalPreviousLocation,
    getLabelByDisciplineType,
    getUnverificationPlatformLabel,
    initializeDisciplineModal,
    invalidateQueries,
    isStrongerDisciplineActive,
    setDisciplineByTab,
  };
};

export {useDiscipline};
